/**
 * HomePage Component
 * Main dashboard/home page.
 *
 * Visual layout:
 *  - PageHeader with greeting + actions
 *  - 4 StatCards for headline metrics
 *  - EmptyState CTA when the platform has zero data
 *  - On mobile, dashboard fills the viewport and avoids body scroll
 */

import { Box, Stack } from '@mui/material';
import {
  Computer as AgentsIcon,
  QueuePlayNext as EnrollmentsIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '@/store/authStore';
import { useEnrollmentsStore } from '@/store/enrollmentsStore';
import { useAgentsStore } from '@/store/agentsStore';
import { useNotificationsStore } from '@/store/notificationsStore';
import PageHeader from '@/components/common/PageHeader';
import EmptyState from '@/components/common/EmptyState';
import StatCard from '@/components/common/StatCard';

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const agents = useAgentsStore((s) => s.agents);
  const agentMetrics = useAgentsStore((s) => s.agentMetrics);
  const enrollments = useEnrollmentsStore((s) => s.enrollments);
  const unreadAlerts = useNotificationsStore((s) => s.unreadTotal);

  const connectedAgentsCount = agents.filter((a) => a.connected).length;
  const enrollmentsCount = enrollments.length;
  const activeSessionsCount = Object.values(agentMetrics || {}).filter(
    (m) => (m as any)?.activeApp?.available
  ).length;

  const totalAgents = agents.length;

  // The greeting line — never produces a dangling ", !" if user is null.
  const displayName = user?.username?.trim();
  const greeting = displayName ? `Bienvenido, ${displayName}!` : 'Bienvenido!';

  // When the platform is brand new, swap the 4 zero-cards for a single
  // empty-state CTA that walks the admin through the first steps.
  const isFirstRun =
    totalAgents === 0 && enrollmentsCount === 0 && activeSessionsCount === 0 && unreadAlerts === 0;

  return (
    <Box
      sx={{
        // Fill the available vertical space inside <main> so the page never
        // produces a stray body scrollbar on mobile.
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
    >
      <PageHeader
        icon={
          <Box
            component="span"
            aria-hidden
            sx={{ fontSize: 22, lineHeight: 1, display: 'inline-flex' }}
          >
            👋
          </Box>
        }
        title={greeting}
        subtitle="Panel de control de Remote Assist"
        actions={
          <Stack direction="row" spacing={1}>
            <StatCardActionButton
              icon={<RefreshIcon fontSize="small" />}
              label="Refrescar"
              onClick={() => window.location.reload()}
            />
          </Stack>
        }
      />

      {isFirstRun ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <EmptyState
            icon={<AgentsIcon />}
            title="Tu flota aún no está conectada"
            description="Empezá por descargar el agente en cada equipo o aprobá un enrollment pendiente. Cuando los agentes se conecten, verás métricas y eventos en vivo acá."
            action={{
              label: 'Descargar agente',
              onClick: () => navigate('/download'),
              startIcon: <DownloadIcon />,
            }}
          />
        </Box>
      ) : (
        // 2×2 en mobile/tablet, 1×4 en desktop.
        // Reordenamos por criticidad operativa cuando hay alertas:
        //   1) Alertas (si hay) o Agentes conectados
        //   2) Enrollments pendientes (acción del admin)
        //   3) Sesiones activas
        //   4) Resto
        // En una grilla 2×2 esto ubica las cosas urgentes arriba a la izquierda.
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 1.25, sm: 2 },
            gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
          }}
        >
          {unreadAlerts > 0 ? (
            <StatCard
              label="Alertas sin leer"
              value={unreadAlerts}
              icon={<WarningIcon />}
              color="error"
            />
          ) : (
            <StatCard
              label="Agentes conectados"
              value={connectedAgentsCount}
              icon={<AgentsIcon />}
              color="primary"
              hint={totalAgents > 0 ? `${totalAgents} en total` : undefined}
              onClick={() => navigate('/agents')}
            />
          )}
          <StatCard
            label="Enrollments"
            value={enrollmentsCount}
            icon={<EnrollmentsIcon />}
            color="warning"
            hint="Pendientes de aprobación"
            onClick={() => navigate('/enrollments')}
          />
          <StatCard
            label="Sesiones activas"
            value={activeSessionsCount}
            icon={<CheckIcon />}
            color="success"
          />
          {unreadAlerts > 0 ? (
            <StatCard
              label="Agentes conectados"
              value={connectedAgentsCount}
              icon={<AgentsIcon />}
              color="primary"
              hint={totalAgents > 0 ? `${totalAgents} en total` : undefined}
              onClick={() => navigate('/agents')}
            />
          ) : (
            <StatCard
              label="Alertas sin leer"
              value={unreadAlerts}
              icon={<WarningIcon />}
              color={unreadAlerts > 0 ? 'error' : 'default'}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

/**
 * Small helper so the action button in the PageHeader matches the rest of
 * the dashboard controls without forcing HomePage to import Button directly.
 */
function StatCardActionButton({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  // Use a plain button element so we don't drag in the full Button theme
  // overrides for a single icon+label affordance.
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        all: 'unset',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.75,
        borderRadius: 1.5,
        fontSize: '0.875rem',
        fontWeight: 500,
        color: 'text.primary',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      {icon}
      {label}
    </Box>
  );
}
